import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { useCart } from "../lib/cart";
import { useI18n } from "../lib/i18n";
import { formatPriceLocal as formatPrice, fixAssetUrl, api } from "../lib/api";
import { Trash2, Plus, Minus, ShoppingBag, Truck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

export default function CartDrawer() {
  const { items, update, remove, subtotal, hasPhysicalGaming, open, setOpen } = useCart();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [shippingFee, setShippingFee] = useState(99);

  useEffect(() => {
    api.get("/site-settings").then((r) => {
      if (typeof r.data?.gaming_shipping_fee === "number") setShippingFee(r.data.gaming_shipping_fee);
    }).catch(() => {});
  }, []);

  const shipping = hasPhysicalGaming ? shippingFee : 0;
  const total = subtotal + shipping;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl">{t("cart_title")}</SheetTitle>
          <SheetDescription className="sr-only">Položky ve vašem nákupním košíku</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto -mx-6 px-6 mt-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <ShoppingBag className="w-12 h-12 text-zinc-300 mb-4"/>
              <div className="text-zinc-600">{t("cart_empty")}</div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {items.map((it) => (
                <div key={`${it.kind || "shop"}-${it.product_id}`} className="flex gap-3 p-3 rounded-xl border border-zinc-200" data-testid={`cart-item-${it.slug}`}>
                  <div className="w-16 h-16 rounded-lg bg-zinc-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {it.image && <img src={fixAssetUrl(it.image)} alt="" className="max-h-full max-w-full object-contain"/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                      <div className="text-sm font-semibold leading-tight line-clamp-2 flex-1">{it.name}</div>
                      {it.kind === "gaming" && (
                        <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 flex-shrink-0">Gaming</span>
                      )}
                    </div>
                    <div className="text-sm text-zinc-600 mt-1">{formatPrice(it.price)}</div>
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => update(it.product_id, it.quantity - 1, it.kind || "shop")} className="w-7 h-7 rounded-full border border-zinc-200 flex items-center justify-center hover:bg-zinc-50" data-testid={`qty-dec-${it.slug}`}><Minus className="w-3 h-3"/></button>
                      <span className="text-sm font-semibold w-6 text-center">{it.quantity}</span>
                      <button onClick={() => update(it.product_id, it.quantity + 1, it.kind || "shop")} className="w-7 h-7 rounded-full border border-zinc-200 flex items-center justify-center hover:bg-zinc-50" data-testid={`qty-inc-${it.slug}`}><Plus className="w-3 h-3"/></button>
                      <button onClick={() => remove(it.product_id, it.kind || "shop")} className="ml-auto p-1.5 text-zinc-400 hover:text-red-600" data-testid={`remove-${it.slug}`}><Trash2 className="w-4 h-4"/></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {items.length > 0 && (
          <div className="border-t border-zinc-200 pt-4 mt-4 -mx-6 px-6">
            <div className="flex justify-between mb-1">
              <span className="text-sm text-zinc-600">{t("subtotal")}</span>
              <span className="text-sm font-semibold">{formatPrice(subtotal)}</span>
            </div>
            {hasPhysicalGaming && (
              <div className="flex justify-between mb-1" data-testid="cart-shipping-row">
                <span className="text-sm text-zinc-600 inline-flex items-center gap-1.5"><Truck className="w-3.5 h-3.5"/> Doručení na adresu</span>
                <span className="text-sm font-semibold">{formatPrice(shipping)}</span>
              </div>
            )}
            <div className="flex justify-between mb-4 mt-2">
              <span className="font-display font-bold text-lg">{t("total")}</span>
              <span className="font-display font-bold text-lg">{formatPrice(total)}</span>
            </div>
            <button onClick={() => { setOpen(false); navigate("/pokladna"); }} className="btn-primary w-full py-3 rounded-full font-semibold" data-testid="goto-checkout-btn">{t("checkout")} →</button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
